import format from 'date-fns/format';

import { RelativePeriod } from '../comparison-period-selector/Types';
import { Periodicity, PeriodicityKey } from '../time-period-selector/PeriodsTypes';
import {
  getDateFromPeriod,
  getNumberOfWeeks,
  getRangeComparisonPeriod,
  getYTDMATComparisonPeriod,
} from '@utils/PeriodUtils';
import { BaseTimePeriod } from '@services/FiltersTypes';

const CALENDAR_QUARTER_PERIOD_DURATION: number = 3;

export enum PeriodFormat {
  // The formats here are based on the date-fns library: https://date-fns.org/v2.30.0/docs/format
  SHORT = 'MMM yyyy',
  LONG = 'MMMM yyyy',
}

const minusOneFromDateString = (selectedPeriod: BaseTimePeriod, current: string) => {
  let previous = (parseInt(current, 10) - 1).toString();

  // eg. adds the '0' in front of '1' to form Week 01
  if (previous.length === 1) {
    previous = `0${previous}`;
  }

  return selectedPeriod.label.replace(current, previous);
};

const getComparisonPeriod = (
  selectedPeriod: BaseTimePeriod,
  selectedPeriodicity: Periodicity | undefined,
  comparisonPeriodType: RelativePeriod | BaseTimePeriod,
  datePeriodFormat: PeriodFormat = PeriodFormat.SHORT,
): string => {
  let comparisonPeriod: string = '';

  if (!selectedPeriod) {
    return comparisonPeriod;
  }

  // Special case handling when selectedPeriod is YTD/MAT
  if (selectedPeriod.value.includes(PeriodicityKey.YTD) || selectedPeriod.value.includes(PeriodicityKey.MAT)) {
    return getYTDMATComparisonPeriod(selectedPeriod.label);
  }

  if (selectedPeriodicity === Periodicity.RollingQuarter) {
    return getRangeComparisonPeriod(selectedPeriod.value, comparisonPeriodType);
  }

  if (selectedPeriodicity === Periodicity.CalendarQuarter) {
    return getCalQuarterComparisonPeriodLabel(comparisonPeriodType, selectedPeriod);
  }

  if (selectedPeriodicity === Periodicity.CustomMonths) {
    const customMonthComparisonPeriod = comparisonPeriodType as BaseTimePeriod;
    return customMonthComparisonPeriod.label;
  }

  if (selectedPeriodicity === Periodicity.CustomWeeks) {
    const customWeekComparisonPeriod = comparisonPeriodType as BaseTimePeriod;
    return customWeekComparisonPeriod.label;
  }
  if (comparisonPeriodType === RelativePeriod.PreviousYearCurrentPeriod) {
    const currentYear = selectedPeriod.label.substring(selectedPeriod.label.length - 4);
    comparisonPeriod = minusOneFromDateString(selectedPeriod, currentYear);
    if (selectedPeriodicity === Periodicity.Weekly) {
      comparisonPeriod = comparisonPeriod.replace('Week ', '');
    }

    return comparisonPeriod;
  }

  if (selectedPeriodicity === Periodicity.Weekly) {
    const currentWeek = selectedPeriod.label.substring(5, selectedPeriod.label.length - 5);
    const currentYear = Number(selectedPeriod.label.substring(8));

    comparisonPeriod = minusOneFromDateString(selectedPeriod, currentWeek);

    // handles situation when current week is Week 01
    if (currentWeek === '01') {
      const numberOfWeeks = getNumberOfWeeks(Number(`20${currentYear - 1}`));
      comparisonPeriod = selectedPeriod.label.replace(currentWeek, numberOfWeeks.toString());
      comparisonPeriod = comparisonPeriod.replace(currentYear.toString(), `${currentYear - 1}`);
    }

    comparisonPeriod = comparisonPeriod.replace('Week ', '');
    return comparisonPeriod;
  }

  const currentMonth = getDateFromPeriod(selectedPeriod.value);
  const previousMonth = currentMonth.setMonth(currentMonth.getMonth() - 1);
  comparisonPeriod = previousMonth ? format(previousMonth, datePeriodFormat) : '';
  return comparisonPeriod;
};

const getCalQuarterPreviousPeriodLabel: (period: BaseTimePeriod) => string = (period) => {
  // We assume that the label would have the following format
  //    Jan - Mar 2023
  // Since calendar quarters cannot contain overlapping periods (e.g. Dec 2022 - Jan 2023), it is
  // safe to assume that the ending month's year equals the starting month's year.
  const monthYear = period.label.split(' ');
  const startPeriodMonth = monthYear[0];
  const endPeriodMonth = monthYear[2];
  const endPeriodYear = Number(monthYear[3]);

  // Derive the previous calendar quarter
  const startDate: Date = getDateFromPeriod(`${startPeriodMonth} ${endPeriodYear}`);
  const endDate: Date = getDateFromPeriod(`${endPeriodMonth} ${endPeriodYear}`);
  startDate.setMonth(startDate.getMonth() - CALENDAR_QUARTER_PERIOD_DURATION);
  endDate.setMonth(endDate.getMonth() - CALENDAR_QUARTER_PERIOD_DURATION);

  return `${format(startDate, 'MMM')} - ${format(endDate, 'MMM yyyy')}`;
};

const getCalQuarterPreviousYearPeriodLabel: (period: BaseTimePeriod) => string = (period) => {
  // We assume that the label would have the following format
  //    Jan - Mar 2023
  // Since calendar quarters cannot contain overlapping periods (e.g. Dec 2022 - Jan 2023), it is
  // safe to assume that the ending month's year equals the starting month's year.
  const monthYear = period.label.split(' ');
  const startPeriodMonth = monthYear[0];
  const endPeriodMonth = monthYear[2];
  const endPeriodYear = Number(monthYear[3]) - 1;
  return `${startPeriodMonth} - ${endPeriodMonth} ${endPeriodYear}`;
};

export const getCalQuarterComparisonPeriodLabel: (
  comparisonPeriod: RelativePeriod | BaseTimePeriod,
  period: BaseTimePeriod,
) => string = (comparisonPeriod, period) => {
  return RelativePeriod.PreviousYearCurrentPeriod === comparisonPeriod
    ? getCalQuarterPreviousYearPeriodLabel(period)
    : getCalQuarterPreviousPeriodLabel(period);
};

export const addStartingYearForCalendarQuarter: (period: string) => string = (period) => {
  // We assume that the label would have the following format
  //    Jan - Mar 2023
  // Since calendar quarters cannot contain overlapping periods (e.g. Dec 2022 - Jan 2023), it is
  // safe to assume that the ending month's year equals the starting month's year.
  const tokens: string[] = period.split(' ');
  const endPeriodYear = Number(tokens[3]);
  tokens.splice(1, 0, `${endPeriodYear}`);

  return tokens.join(' ');
};

export default getComparisonPeriod;
